import type WaDialog from '@awesome.me/webawesome/dist/components/dialog/dialog.js'
import type { DIDURL } from 'iso-did/types'
import type { Accessor, Component } from 'solid-js'
import { buf2hex } from '@wovin/utils'
import { Logger } from 'besonders-logger'
import { DIDKey } from 'iso-did/key'
import { verify } from 'iso-signatures/verifiers/eddsa.js'
import { createEdDSAkeypair, createMnemonic, encHashedMnemonic, getECDHkeypairFromHashArray, importEd25519Key, testStats } from '@wovin/crypto'
import { createEffect, createSignal, For, Show } from 'solid-js'
import { getMnemonicFromIDB, storeKeyPairInIDB } from '../../data/agent/AgentCrypto'
import { useAgent } from '../../data/agent/AgentState'
import { notifyToast } from '../../ui/utils-ui'
import { Iconify } from '../mini-components'
import { TrustKeyDialog } from './TrustKeyDialog'

const { WARN, LOG, DEBUG, VERBOSE, ERROR } = Logger.setup(Logger.INFO)

const inputStyle = {
	'--wa-input-font-size-small': '11px',
}
const exampleMnem
	= 'basket,chief,toad,chemical,little,analysis,admonish,cupcake,diatribe,obedient,craving,nightcap,hopeful,street,raggedy,stymie,mechanic,papyrus,sorcerer,plushy,rickshaw,taffy,nocturne,unicycle'
export const SingleWord: Component<{
	index: number
	mnemArray: Accessor<string[]>
}> = function SingleWord(props) {
	const [myWord, setMyword] = createSignal(props.mnemArray()[props.index])

	const onInput = (evt: InputEvent) => {
		setMyword((evt.target as HTMLInputElement).value)
	}
	return (
		<wa-input
			placeholder='- - -'
			value={`${props.index}: ${myWord()}`}
			onInput={onInput}
			filled
			font-mono
			size='s'
			w-24
			mb-4
			mx-1
			disabled
			spellcheck={false}
			style={inputStyle}
		/>
	)
}
export const MnemDialog: Component<{
	// mnemDialogRef: any
}> = function MnemDialog() {
	const [storedMnemonic, setStoredMnemonic] = createSignal()
	const [isKeyMatching, setMatching] = createSignal(false)
	const [mnemOnScreen, setMnem] = createSignal('')
	const [mnemArray, setMnemArr] = createSignal([])
	createEffect(() => {
		const cleanedMnemString = mnemOnScreen().replaceAll(' ', ',').replaceAll(',,,', ',').replaceAll(',,', ',')
		setMnemArr(cleanedMnemString.split(','))
		VERBOSE('cleaned array', mnemArray())
	})
	const onInput = (evt: InputEvent) => {
		const currentMnemFromField = (evt.target as HTMLInputElement).value
		if (currentMnemFromField) setMnem(currentMnemFromField)
		else notifyToast(`24 valid words are needed`)
	}
	const comparePubDerivationKey = async (hexPubKey: string, newMnemArr?: string[]) => {
		const agent = useAgent()

		const { publicKey } = await agent.getOrSetDerivationKeypair(newMnemArr)
		const exportedPubKeyHex = buf2hex(await globalThis.crypto.subtle.exportKey('raw', publicKey))
		const isMatching = !!(hexPubKey === exportedPubKeyHex)
		DEBUG({ isMatching, hexPubKey, exportedPubKeyHex })
		return isMatching
	}
	const compareDerivedKeys = async () => {
		const { publicKey } = await getECDHkeypairFromHashArray(encHashedMnemonic(mnemArray()))
		DEBUG({ publicKey }, mnemArray())
		const isMatching = await comparePubDerivationKey(buf2hex(await globalThis.crypto.subtle.exportKey('raw', publicKey)))
		if (isMatching) {
			setMatching(true)
			notifyToast(`Looking good, your public key in IDB matches the one derived from the mnemonic you entered`, 'success', 10000)
		} else {
			notifyToast(`UhOh, your pubkey DOES NOT MATCH the one derived from the mnemonic`, 'danger', 10000)
		}
	}

	const afterhide = async (hideargs) => {
		DEBUG({ hideargs })
	}
	const testSigning = async (privateKey: CryptoKey, publicKey: CryptoKey, didURLString: DIDURL, data = 'test123') => {
		const did = DIDKey.fromString(didURLString)
		LOG.group('Test Signing with Trust Key', async () => {
			LOG({ privateKey, publicKey, did })
			const encoder = new TextEncoder()
			const encodedData = encoder.encode(data)
			const encodedWrongData = encoder.encode(`${data}WRONG`)
			const signature = await crypto.subtle.sign(
				{ name: 'Ed25519' },
				privateKey,
				encodedData,
			)

			// Log the first part of the signature data
			const signatureHead = new Uint8Array(signature, 0, 8)
			LOG(`signatureHead: ${signatureHead}...[${signature.byteLength} bytes total]`, { signature })

			// Verify the signature using the public key
			const verifyResult = await crypto.subtle.verify(
				{ name: 'Ed25519' },
				publicKey,
				signature,
				encodedData,
			)

			// verify the signature using the did object returned by the
			const verifiedViaDID = await verify({
				signature: new Uint8Array(signature),
				message: encodedData,
				did,
			})

			// Verify the signature using the public key via browser subtle crypto
			const unverifiableResult = await crypto.subtle.verify(
				{ name: 'Ed25519' },
				publicKey,
				signature,
				encodedWrongData,
			)
			WARN(`signature verification?:`, { verifiedViaDID, verifyResult, unverifiableResult })
		})
	}
	const dealWithStoredMnem = async () => {
		const mnemFromIDB = await getMnemonicFromIDB(useAgent())
		DEBUG({ mnemFromIDB })
		setStoredMnemonic(mnemFromIDB)
		setMnem(mnemFromIDB)
		notifyToast('You should really save the mnemonic so we can delete it from IDB (even if it is encrypted)')
	}
	const deriveTrustKey = async (saltString = '') => {
		if (!mnemOnScreen()) await dealWithStoredMnem()
		if (!mnemArray() || mnemArray().length < 24) {
			notifyToast('No mnemonic on screen or in IDB')
			throw ERROR('No mnemonic on screen or in IDB', { mnemOnScreen: mnemOnScreen(), mnemArray: mnemArray() })
		}
		const trustKey = await createEdDSAkeypair(mnemArray(), saltString)
		const rawExportedKey = trustKey.export()
		const keyPair = await importEd25519Key(trustKey)
		DEBUG.force({ trustKey, rawExportedKey, keyPair })
		const { privateKey, publicKey } = keyPair

		testSigning(privateKey, publicKey, trustKey.verifiableDid.didUrl)
		storeKeyPairInIDB(keyPair, useAgent().ag)
	}

	const importDerivationKey = async (newMnemArr?: string[]) => {
		const { publicKey } = await useAgent().getOrSetDerivationKeypair(newMnemArr)
		const exportedPubKeyHex = buf2hex(await globalThis.crypto.subtle.exportKey('raw', publicKey))
		DEBUG({ exportedPubKeyHex })
	}

	let mnemDialogRef: WaDialog
	return (
		<>
			<wa-button
				max-w-fit
				// variant='brand'
				onClick={() => (mnemDialogRef as any)?.show()}
				size='s'
			>
				<div flex='~ items-center' gap-1>
					<Iconify slot='start' name='gear' size={4} />
					Mnemonic Key
				</div>
			</wa-button>
			<wa-dialog
				ref={mnemDialogRef}
				onwa-after-hide={afterhide}
				class='mnemDialog'
				w='90%'
			>
				<style>
					{`
					.mnemDialog::part(title) {
						padding: 0px;
					}
					.mnemDialog::part(panel) {
						width: 80%;
					}
					.mnemDialog::part(header) {
						padding: 1em 1em 0.5em 1em ;
					}
					.mnemDialog::part(body) {
						padding:  0.5em 1em 1em 1em;
					}
				`}
				</style>
				<h4 slot='label' py-1 px-2 m-0>
					Enter a valid 24 word mnemonic:
				</h4>
				<wa-input
					placeholder='list of words like foo,bar,baz (separated by single commas or single spaces)'
					value={mnemOnScreen()}
					onInput={onInput}
					filled
					font-mono
					size='s'
					max-w-full
					min-w-fit
					mb-4
					spellcheck={false}
					style={inputStyle}
				/>
				<Show when={mnemArray().length > 1}>
					<div flex='~ wrap justify-between'>
						<For
							each={mnemArray().reduce((result, value, index, array) => {
								if (index % 2 === 0) {
									result.push(array.slice(index, index + 2))
								}
								return result
							}, [])}
						>
							{(_item, eachIndex) => {
								return (
									<div flex='~ row wrap-none'>
										<SingleWord index={eachIndex() * 2} mnemArray={mnemArray} />
										<SingleWord index={eachIndex() * 2 + 1} mnemArray={mnemArray} />
									</div>
								)
							}}
						</For>
					</div>
				</Show>
				<div gap-4 flex='~ items-start'>
					<wa-button
						max-w-fit
						variant={isKeyMatching() ? 'success' : 'default'}
						onClick={compareDerivedKeys}
						size='s'
					>
						<div flex='~ items-center' gap-1>
							<Iconify size={4} slot='start' name={isKeyMatching() ? 'check-circle' : 'question'} />
							Check
						</div>
					</wa-button>
					<wa-button
						max-w-fit
						variant='brand'
						onClick={async () => {
							setMnem(createMnemonic(24).join(',')) // in Memory signal only
							testStats(256, 10)
						}}
						size='s'
					>
						<div flex='~ items-center' gap-1>
							<Iconify size={4} slot='start' name='plus-circle' />
							New
						</div>
					</wa-button>
					<wa-button
						max-w-fit
						variant='brand'
						onClick={async () => {
							importDerivationKey(mnemArray())
							testStats(256, 10)
						}}
						size='s'
					>
						<div flex='~ items-center' gap-1>
							<Iconify slot='start' size='4' name='shield-plus' />
							Import
						</div>
					</wa-button>
					<wa-button
						max-w-fit
						variant='danger'
						onClick={dealWithStoredMnem}
						size='s'
					>
						<div flex='~ items-center' gap-1>
							<Iconify slot='start' size='4' name='shield-warning' />
							Export
						</div>
					</wa-button>
					{/* <wa-button
						max-w-fit
						variant='danger'
						onClick={deriveTrustKey}
						size='s'
					>
						<div flex='~ items-center' gap-1>
							<Iconify slot='start' size='4' name='shield-warning' />
							Trust Key
						</div>
					</wa-button> */}
					<TrustKeyDialog
						actionFx={deriveTrustKey}
					/>
				</div>
			</wa-dialog>
		</>
	)
}
